import { Box, Card, CardContent, Typography, Link as MuiLink, Chip } from "@mui/material";
import { Link } from "@/i18n/routing";

interface CardBoxProps {
    article: {
        title: string;
        description: string;
        publishedDate: string;
        sourceLink?: string;
        category?: string;
        coverImage?: {
            fields?: {
                file?: {
                    url?: string;
                };
            };
        };
    };
    id: string;
    basePath?: string;
}

const CardBox = ({ article, id, basePath = "/news" }: CardBoxProps) => {
    const { title, description, publishedDate, sourceLink, category, coverImage } = article;

    const imageUrl = coverImage?.fields?.file?.url ? `https:${coverImage.fields.file.url}` : null;

    // Direct formatting for SEO and performance. Suppress hydration warning if locale differs.
    const cleanDate = publishedDate ? publishedDate.split('T')[0] : '';
    const formattedDate = cleanDate ? new Date(cleanDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC'
    }) : '';

    const cardContent = (
        <Card sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            transition: 'transform 0.2s',
            borderRadius: 2,
            overflow: 'hidden',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4
            }
        }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography
                        variant="body2"
                        component="time"
                        color="text.secondary"
                        gutterBottom
                        suppressHydrationWarning // Handle potential locale differences between server/client
                    >
                        {formattedDate}
                    </Typography>
                    <Chip
                        label={category || "News"}
                        sx={{
                            bgcolor: "#F15859",
                            color: "white",
                            fontWeight: 700,
                            fontSize: "12px",
                        }}
                    />
                </Box>
                <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{
                    mb: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                }}>
                    {description}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                    <Typography variant="button" color="primary" sx={{ fontWeight: 'bold' }}>
                        Read More &rarr;
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );

    const href = sourceLink || `${basePath}/${id}`;
    const isExternal = !!sourceLink;

    return (
        <Box sx={{ height: '100%' }}>
            {isExternal ? (
                <MuiLink href={href} target="_blank" rel="noopener noreferrer" underline="none" aria-label={`Read more about ${title}`}>
                    {cardContent}
                </MuiLink>
            ) : (
                <MuiLink component={Link} href={href} underline="none" aria-label={`Read more about ${title}`}>
                    {cardContent}
                </MuiLink>
            )}
        </Box>
    );
};

export default CardBox;